import type { LegendSpec } from "../lib/sources";

export interface LegendBarOptions {
  /** The mobile-only opacity slider element, embedded here per DESIGN.md §5 progressive disclosure. */
  opacitySliderElement: HTMLElement;
}

/**
 * Collapsible precipitation-intensity legend (DESIGN.md §6-7, §9 "Brak
 * koloru = brak opadów"; M003 Aneks §R3: per-region palette). The gradient,
 * threshold ticks, stop swatches, title and source note are all rebuilt by
 * `setSource()` from the active RadarSource's LegendSpec — the constructor
 * only builds the static DOM skeleton. Word labels ("słabe/umiarkowane/
 * intensywne") are intentionally fixed regardless of region.
 */
export class LegendBar {
  readonly element: HTMLElement;
  private readonly detailsEl: HTMLDivElement;
  private readonly summary: HTMLButtonElement;
  private readonly gradientEl: HTMLSpanElement;
  private readonly gradientTicksEl: HTMLDivElement;
  private readonly stopsWrapEl: HTMLDivElement;
  private readonly scaleFirstEl: HTMLSpanElement;
  private readonly scaleLastEl: HTMLSpanElement;
  private readonly scaleTitleEl: HTMLParagraphElement;
  private readonly noteSourceEl: HTMLParagraphElement;
  private expanded = false;

  constructor(opts: LegendBarOptions) {
    this.element = document.createElement("div");
    this.element.className = "legend card";

    this.summary = document.createElement("button");
    this.summary.type = "button";
    this.summary.className = "legend__summary";
    this.summary.setAttribute("aria-expanded", "false");
    this.summary.setAttribute("aria-controls", "legend-details");
    this.summary.setAttribute("aria-label", "Legenda intensywności opadów — rozwiń szczegóły");

    const gradientWrap = document.createElement("div");
    gradientWrap.className = "legend__gradient-wrap";

    this.gradientEl = document.createElement("span");
    this.gradientEl.className = "legend__gradient";
    this.gradientEl.setAttribute("aria-hidden", "true");

    // Threshold ticks under the gradient, at the source's own stop
    // boundaries (§A6) — never invented, only positions from the active
    // LegendSpec (see src/lib/legend.ts header notes for what each source's
    // stops are actually derived from).
    this.gradientTicksEl = document.createElement("div");
    this.gradientTicksEl.className = "legend__gradient-ticks";
    this.gradientTicksEl.setAttribute("aria-hidden", "true");

    gradientWrap.append(this.gradientEl, this.gradientTicksEl);
    this.summary.appendChild(gradientWrap);

    const words = document.createElement("div");
    words.className = "legend__words";
    words.setAttribute("aria-hidden", "true");
    for (const w of ["słabe", "umiarkowane", "intensywne"]) {
      const span = document.createElement("span");
      span.textContent = w;
      words.appendChild(span);
    }

    this.detailsEl = document.createElement("div");
    this.detailsEl.className = "legend__details";
    this.detailsEl.id = "legend-details";
    this.detailsEl.hidden = true;

    this.stopsWrapEl = document.createElement("div");
    this.stopsWrapEl.className = "legend__stops";
    this.stopsWrapEl.setAttribute("aria-hidden", "true");

    const scaleLabels = document.createElement("div");
    scaleLabels.className = "legend__scale-labels";
    this.scaleFirstEl = document.createElement("span");
    this.scaleFirstEl.className = "tabular-nums";
    this.scaleLastEl = document.createElement("span");
    this.scaleLastEl.className = "tabular-nums";
    scaleLabels.append(this.scaleFirstEl, this.scaleLastEl);

    this.scaleTitleEl = document.createElement("p");
    this.scaleTitleEl.className = "legend__note";

    const noteEmpty = document.createElement("p");
    noteEmpty.className = "legend__note";
    noteEmpty.textContent = "Brak koloru = brak opadów.";

    this.noteSourceEl = document.createElement("p");
    this.noteSourceEl.className = "legend__note";

    const opacitySection = document.createElement("div");
    opacitySection.className = "legend__opacity";
    opacitySection.appendChild(opts.opacitySliderElement);

    this.detailsEl.append(
      this.scaleTitleEl,
      this.stopsWrapEl,
      scaleLabels,
      noteEmpty,
      this.noteSourceEl,
      opacitySection
    );

    this.summary.addEventListener("click", () => this.toggle());

    this.element.append(this.summary, words, this.detailsEl);
  }

  toggle(): void {
    this.expanded = !this.expanded;
    this.detailsEl.hidden = !this.expanded;
    this.summary.setAttribute("aria-expanded", String(this.expanded));
  }

  /**
   * (Re)paint the whole legend from a RadarSource's LegendSpec — called once
   * at boot and again on every region switch (DESIGN.md Aneks M003 §R2 step
   * 3 "atomowa podmiana tożsamości", §R3). When `legend.unit` is present
   * (DWD publishes real dBZ thresholds) the end labels show actual values
   * and each swatch gets a `title` with its dBZ range instead of the
   * word-only "słabsze/silniejsze" the US continuous composite uses.
   */
  setSource(legend: LegendSpec): void {
    this.gradientEl.style.background = legend.gradientCss;

    this.gradientTicksEl.innerHTML = "";
    const stopCount = legend.stops.length;
    for (let i = 1; i < stopCount; i++) {
      const tick = document.createElement("span");
      tick.style.left = `${(i / stopCount) * 100}%`;
      this.gradientTicksEl.appendChild(tick);
    }

    this.stopsWrapEl.innerHTML = "";
    for (let i = 0; i < legend.stops.length; i++) {
      const stop = legend.stops[i];
      const seg = document.createElement("span");
      seg.style.background = stop.color;
      if (legend.unit !== undefined && stop.dbz !== undefined) {
        const next = legend.stops[i + 1]?.dbz;
        seg.title = next !== undefined ? `${stop.dbz}–${next} ${legend.unit}` : `≥${stop.dbz} ${legend.unit}`;
      }
      this.stopsWrapEl.appendChild(seg);
    }

    if (legend.unit !== undefined && legend.stops[0]?.dbz !== undefined) {
      const last = legend.stops[legend.stops.length - 1];
      this.scaleFirstEl.textContent = `${legend.stops[0].dbz} ${legend.unit}`;
      this.scaleLastEl.textContent = `≥${last.dbz} ${legend.unit}`;
    } else {
      this.scaleFirstEl.textContent = "słabsze";
      this.scaleLastEl.textContent = "silniejsze";
    }

    this.scaleTitleEl.textContent = legend.scaleTitle;
    this.noteSourceEl.textContent = legend.sourceNote;
  }
}
